/**
 * Persona Detail Page
 *
 * Full persona detail view with:
 * - PersonaViewer component
 * - Profile link
 * - Metadata display
 * - Actions (regenerate, delete)
 */

import { useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  RefreshCw,
  Trash2,
  ExternalLink,
  Clock,
  User,
  AlertCircle,
  Cpu,
  ChevronDown,
  Shield,
} from 'lucide-react'
import { usePersona, useGeneratePersona, useDeletePersona } from '@/hooks/usePersonas'
import PersonaViewer from '@/components/personas/PersonaViewer'
import { ModeBadge } from '@/components/personas/PersonaModeSelector'
import { ENGINE_LABELS, ENGINE_SHORT_LABELS } from '@personic/shared/types/persona-engine'

export default function PersonaDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: persona, isLoading, error } = usePersona(id || '')
  const generatePersona = useGeneratePersona()
  const deletePersona = useDeletePersona()

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showEngineDetails, setShowEngineDetails] = useState(false)

  const handleRegenerate = async () => {
    if (!persona) return

    try {
      await generatePersona.mutateAsync({
        profileId: String(persona.source_profile_id),
        productContext: persona.product_context,
        sensitivityMode: persona.sensitivity_mode as 'full' | 'partial' | 'minimal',
      })
      // Refresh will happen automatically via query invalidation
    } catch (err) {
      console.error('Failed to regenerate:', err)
    }
  }

  const handleDelete = async () => {
    if (!id) return

    try {
      await deletePersona.mutateAsync(id)
      navigate('/tenant/personas')
    } catch (err) {
      console.error('Failed to delete:', err)
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-6 w-32 bg-gray-200 rounded mb-4" />
          <div className="h-8 w-64 bg-gray-200 rounded" />
        </div>
        <div className="card p-6 animate-pulse">
          <div className="h-6 w-48 bg-gray-200 rounded mb-4" />
          <div className="space-y-3">
            <div className="h-4 w-full bg-gray-200 rounded" />
            <div className="h-4 w-3/4 bg-gray-200 rounded" />
            <div className="h-4 w-1/2 bg-gray-200 rounded" />
          </div>
        </div>
      </div>
    )
  }

  if (error || !persona) {
    return (
      <div className="space-y-6">
        <Link
          to="/tenant/personas"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Personas
        </Link>

        <div className="card p-12 text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Persona Not Found</h3>
          <p className="text-gray-500 mb-4">
            The persona you're looking for doesn't exist or has been deleted.
          </p>
          <Link
            to="/tenant/personas"
            className="inline-flex items-center gap-2 px-4 py-2 bg-personic-600 text-white rounded-lg hover:bg-personic-700 transition-colors"
          >
            Browse Personas
          </Link>
        </div>
      </div>
    )
  }

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleString('en-US', {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    } catch {
      return 'Unknown'
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <Link
            to="/tenant/personas"
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Personas
          </Link>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-3">
            <span className="capitalize">{persona.product_context}</span>
            <span className="text-gray-400">Persona</span>
            <ModeBadge mode={persona.sensitivity_mode} />
          </h2>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleRegenerate}
            disabled={generatePersona.isPending}
            className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw
              className={`w-4 h-4 ${generatePersona.isPending ? 'animate-spin' : ''}`}
            />
            Regenerate
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="flex items-center gap-2 px-4 py-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      </div>

      {/* Metadata Bar */}
      <div className="card px-6 py-4 flex flex-wrap items-center gap-6">
        <div className="flex items-center gap-2 text-sm">
          <User className="w-4 h-4 text-gray-400" />
          <span className="text-gray-500">Profile:</span>
          <Link
            to={`/tenant/profiles/${persona.source_profile_id}`}
            className="text-personic-600 hover:text-personic-700 font-mono"
          >
            {persona.source_profile_id}
            <ExternalLink className="w-3 h-3 inline ml-1" />
          </Link>
        </div>

        <div className="flex items-center gap-2 text-sm">
          <Clock className="w-4 h-4 text-gray-400" />
          <span className="text-gray-500">Generated:</span>
          <span className="text-gray-900">{formatDate(persona.generated_at)}</span>
        </div>

        {persona.last_used && (
          <div className="flex items-center gap-2 text-sm">
            <Clock className="w-4 h-4 text-gray-400" />
            <span className="text-gray-500">Last Used:</span>
            <span className="text-gray-900">{formatDate(persona.last_used)}</span>
          </div>
        )}

        {persona.use_count !== undefined && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500">Use Count:</span>
            <span className="text-gray-900">{persona.use_count}</span>
          </div>
        )}

        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-500">Version:</span>
          <span className="text-gray-900">v{persona.version}</span>
        </div>

        {persona.is_expired && (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">
            <AlertCircle className="w-3 h-3" />
            Expired
          </span>
        )}

        {!persona.is_current && !persona.is_expired && (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs font-medium">
            Superseded
          </span>
        )}
      </div>

      {/* Engine Details (when Persona Engine data available) */}
      {(persona.engine_algorithm_version || persona.engine_confidence_score != null) && (
        <div className="card overflow-hidden">
          <button
            onClick={() => setShowEngineDetails(!showEngineDetails)}
            className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <Cpu className="w-5 h-5 text-personic-600" />
              <div className="text-left">
                <h3 className="font-semibold text-gray-900">Engine Details</h3>
                <div className="flex items-center gap-3 mt-0.5">
                  {persona.engine_algorithm_version && (
                    <span className="text-xs font-mono text-gray-500">
                      {persona.engine_algorithm_version}
                    </span>
                  )}
                  {persona.engine_confidence_score != null && (
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                      persona.engine_confidence_score >= 0.7
                        ? 'bg-emerald-100 text-emerald-700'
                        : persona.engine_confidence_score >= 0.45
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-red-100 text-red-700'
                    }`}>
                      Confidence: {(persona.engine_confidence_score * 100).toFixed(0)}%
                    </span>
                  )}
                </div>
              </div>
            </div>
            <ChevronDown
              className={`w-5 h-5 text-gray-400 transition-transform ${
                showEngineDetails ? 'rotate-180' : ''
              }`}
            />
          </button>

          {showEngineDetails && (
            <div className="px-6 pb-6 border-t border-gray-100">
              <div className="grid md:grid-cols-3 gap-4 pt-4">
                {/* Engine Name */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Engine</p>
                  {persona.engine_algorithm_version ? (
                    <div>
                      <p className="font-semibold text-gray-900">
                        {ENGINE_SHORT_LABELS[persona.engine_algorithm_version.split('_v')[0] as keyof typeof ENGINE_SHORT_LABELS] || persona.engine_algorithm_version.split('_v')[0].toUpperCase()}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {ENGINE_LABELS[persona.engine_algorithm_version.split('_v')[0] as keyof typeof ENGINE_LABELS] || 'Unknown engine'}
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">N/A</p>
                  )}
                </div>

                {/* Algorithm Version */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Algorithm Version</p>
                  <p className="font-mono text-sm text-gray-900">
                    {persona.engine_algorithm_version || 'N/A'}
                  </p>
                </div>

                {/* Confidence Score */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Confidence Score</p>
                  {persona.engine_confidence_score != null ? (
                    <div>
                      <p className="font-semibold text-gray-900">
                        {(persona.engine_confidence_score * 100).toFixed(1)}%
                      </p>
                      <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all ${
                            persona.engine_confidence_score >= 0.7
                              ? 'bg-emerald-500'
                              : persona.engine_confidence_score >= 0.45
                              ? 'bg-amber-500'
                              : 'bg-red-500'
                          }`}
                          style={{ width: `${persona.engine_confidence_score * 100}%` }}
                        />
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">N/A</p>
                  )}
                </div>
              </div>

              {/* Framework Config (if available in persona data) */}
              {persona.framework_config && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-2">Framework Configuration</p>
                  <div className="flex flex-wrap gap-2">
                    {(persona.framework_config.modifiers as string[] | undefined)?.map((mod: string) => (
                      <span key={mod} className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-gray-200 rounded text-xs text-gray-600">
                        <Shield className="w-3 h-3" />
                        {mod.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Persona Viewer */}
      <PersonaViewer
        persona={persona}
        onExport={(format) => {
          console.log('Exported as', format)
        }}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Delete Persona?
            </h3>
            <p className="text-gray-600 mb-4">
              This will permanently delete this persona. This action cannot be undone.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deletePersona.isPending}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {deletePersona.isPending && (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                )}
                Delete Persona
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
